<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import dayjs from 'dayjs';
import {
  ATTENDANCE_PERMISSIONS,
  ASSIGNMENT_POLICY_PERMISSIONS,
} from 'dashboard/constants/permissions';

import WorkforceSelfStatusCard from './components/WorkforceSelfStatusCard.vue';
import WorkforceAPI from 'dashboard/api/workforce';

const route = useRoute();
const store = useStore();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();
const { checkPermissions } = usePolicy();

const currentUser = useMapGetter('getCurrentUser');
// Navigation is handled by the left sidebar; keep layout content-focused.

const selfStatus = ref(null);
const loadingSelfStatus = ref(false);
const actionBusy = ref(false);
const now = ref(dayjs());
const statusPanelExpanded = ref(true);
let ticker = null;

const fetchSelfStatus = async () => {
  if (!currentUser.value?.id) {
    selfStatus.value = null;
    return;
  }

  loadingSelfStatus.value = true;
  try {
    const response = await WorkforceAPI.getLiveStatus({
      user_ids: currentUser.value.id,
    });
    selfStatus.value = response?.agents?.[0] || null;
  } catch (error) {
    useAlert(t('WORKFORCE.TOASTS.SELF_STATUS_ERROR'));
  } finally {
    loadingSelfStatus.value = false;
  }
};

onMounted(async () => {
  await Promise.all([store.dispatch('agents/get')]);
  fetchSelfStatus();
  ticker = setInterval(() => {
    now.value = dayjs();
  }, 1000);
});

onBeforeUnmount(() => {
  if (ticker) {
    clearInterval(ticker);
    ticker = null;
  }
  if (feedbackTimer) {
    clearTimeout(feedbackTimer);
    feedbackTimer = null;
  }
});

const hasOpenSession = computed(() => {
  return Boolean(
    selfStatus.value?.session && !selfStatus.value.session.ended_at
  );
});

const inBreak = computed(() => {
  return Boolean(selfStatus.value?.session?.active_break);
});

const nextShiftEndLabel = computed(() => {
  const remainingSeconds = selfStatus.value?.session?.remaining_shift_seconds;
  if (!remainingSeconds || remainingSeconds <= 0)
    return t('WORKFORCE.STATUS.NO_SCHEDULE');

  const minutes = Math.floor(remainingSeconds / 60);
  if (minutes < 60) {
    return t('WORKFORCE.STATUS.ENDS_IN_MIN', { count: minutes });
  }

  const hours = Math.floor(minutes / 60);
  const residualMinutes = minutes % 60;
  return t('WORKFORCE.STATUS.ENDS_IN_HR_MIN', {
    hours,
    minutes: residualMinutes,
  });
});

const currentTimeLabel = computed(() => now.value.format('HH:mm:ss'));

const shiftDurationLabel = computed(() => {
  if (!hasOpenSession.value) {
    return t('WORKFORCE.STATUS.NO_ACTIVE_SHIFT');
  }
  const startedAt = selfStatus.value?.session?.started_at;
  if (!startedAt) return '--';
  const started = dayjs(startedAt);
  if (!started.isValid()) return '--';
  const diffSeconds = Math.max(now.value.diff(started, 'second'), 0);
  const hours = Math.floor(diffSeconds / 3600);
  const minutes = Math.floor((diffSeconds % 3600) / 60);
  const seconds = diffSeconds % 60;
  const parts = [];
  if (hours) parts.push(`${hours}h`);
  parts.push(`${minutes.toString().padStart(2, '0')}m`);
  parts.push(`${seconds.toString().padStart(2, '0')}s`);
  return parts.join(' ');
});

const activeBreakDurationLabel = computed(() => {
  if (!inBreak.value) {
    return t('WORKFORCE.STATUS.NO_ACTIVE_BREAK');
  }
  const startedAt =
    selfStatus.value?.session?.metadata?.active_break_started_at;
  if (!startedAt) return '--';
  const started = dayjs(startedAt);
  if (!started.isValid()) return '--';
  const minutes = Math.max(now.value.diff(started, 'minute'), 0);
  return t('WORKFORCE.STATUS.BREAK_ELAPSED_MINUTES', { count: minutes });
});

const activeTabName = computed(() => route.name);

const actionConfig = computed(() => {
  if (!currentUser.value) {
    return { primary: null, secondary: null };
  }

  if (!hasOpenSession.value) {
    return {
      primary: {
        key: 'check_in',
        label: t('WORKFORCE.ACTIONS.START_SHIFT'),
        handler: () => handleAttendanceAction('check_in'),
        icon: 'i-lucide-play',
      },
      secondary: null,
    };
  }

  if (inBreak.value) {
    return {
      primary: {
        key: 'end_break',
        label: t('WORKFORCE.ACTIONS.END_BREAK'),
        handler: () => handleAttendanceAction('end_break'),
        icon: 'i-lucide-coffee',
      },
      secondary: {
        key: 'check_out',
        label: t('WORKFORCE.ACTIONS.END_SHIFT'),
        handler: () => handleAttendanceAction('check_out'),
        icon: 'i-lucide-stop-circle',
        variant: 'ghost',
      },
    };
  }

  return {
    primary: {
      key: 'start_break',
      label: t('WORKFORCE.ACTIONS.START_BREAK'),
      handler: () => handleAttendanceAction('start_break'),
      icon: 'i-lucide-mug-hot',
    },
    secondary: {
      key: 'check_out',
      label: t('WORKFORCE.ACTIONS.END_SHIFT'),
      handler: () => handleAttendanceAction('check_out'),
      icon: 'i-lucide-stop-circle',
      variant: 'ghost',
    },
  };
});

const resolveAttendanceErrorMessage = error => {
  const code = error?.response?.data?.error;
  if (!code) return null;
  const dictionary = {
    session_already_open: t('WORKFORCE.TOASTS.SESSION_ALREADY_OPEN'),
    no_open_session: t('WORKFORCE.TOASTS.NO_OPEN_SESSION'),
    break_already_active: t('WORKFORCE.TOASTS.BREAK_ALREADY_ACTIVE'),
    break_not_active: t('WORKFORCE.TOASTS.BREAK_NOT_ACTIVE'),
    forbidden: t('WORKFORCE.TOASTS.PERMISSION_ERROR'),
    workforce_management_disabled: t('WORKFORCE.TOASTS.PERMISSION_ERROR'),
  };
  return dictionary[code] || (typeof code === 'string' ? code : null);
};

const handleSoftAttendanceError = async (action, error) => {
  const code = error?.response?.data?.error;
  if (!code) return false;

  if (action === 'check_in' && code === 'session_already_open') {
    await fetchSelfStatus();
    useAlert(t('WORKFORCE.TOASTS.CHECK_IN_SUCCESS'));
    return true;
  }

  if (action === 'check_out' && code === 'no_open_session') {
    await fetchSelfStatus();
    useAlert(t('WORKFORCE.TOASTS.CHECK_OUT_SUCCESS'));
    return true;
  }

  if (action === 'start_break' && code === 'break_already_active') {
    await fetchSelfStatus();
    useAlert(t('WORKFORCE.TOASTS.START_BREAK_SUCCESS'));
    return true;
  }

  if (action === 'end_break' && code === 'break_not_active') {
    await fetchSelfStatus();
    useAlert(t('WORKFORCE.TOASTS.END_BREAK_SUCCESS'));
    return true;
  }

  return false;
};

const handleAttendanceAction = async action => {
  if (!currentUser.value?.id) return;

  actionBusy.value = true;
  const payload = {
    user_id: currentUser.value.id,
    timestamp: new Date().toISOString(),
    source: 'manual',
  };

  try {
    switch (action) {
      case 'check_in':
        await WorkforceAPI.checkIn(payload);
        useAlert(t('WORKFORCE.TOASTS.CHECK_IN_SUCCESS'));
        break;
      case 'check_out':
        await WorkforceAPI.checkOut(payload);
        useAlert(t('WORKFORCE.TOASTS.CHECK_OUT_SUCCESS'));
        break;
      case 'start_break':
        await WorkforceAPI.startBreak(payload);
        useAlert(t('WORKFORCE.TOASTS.START_BREAK_SUCCESS'));
        break;
      case 'end_break':
        await WorkforceAPI.endBreak(payload);
        useAlert(t('WORKFORCE.TOASTS.END_BREAK_SUCCESS'));
        break;
      default:
        break;
    }
    await fetchSelfStatus();
  } catch (error) {
    const handled = await handleSoftAttendanceError(action, error);
    if (handled) {
      return;
    }
    const message =
      resolveAttendanceErrorMessage(error) ||
      t('WORKFORCE.TOASTS.ACTION_ERROR');
    useAlert(message);
    throw error;
  } finally {
    actionBusy.value = false;
  }
};

const primaryAction = computed(() => actionConfig.value.primary);
const secondaryAction = computed(() => actionConfig.value.secondary);

const classifyActionType = action => {
  if (!action) return 'other';
  if (['check_in', 'check_out'].includes(action.key)) return 'shift';
  if (['start_break', 'end_break'].includes(action.key)) return 'break';

  const label = action?.label?.toLowerCase?.() || '';
  if (label.includes('shift') || label.includes('check')) return 'shift';
  if (label.includes('break')) return 'break';
  return 'other';
};

const actionsList = computed(() => {
  const list = [];
  if (primaryAction.value) list.push(primaryAction.value);
  if (secondaryAction.value) list.push(secondaryAction.value);
  return list;
});

const groupedActions = computed(() => {
  let shift = null;
  let breakAction = null;
  const others = [];
  actionsList.value.forEach(action => {
    const type = classifyActionType(action);
    if (type === 'shift' && !shift) {
      shift = action;
      return;
    }
    if (type === 'break' && !breakAction) {
      breakAction = action;
      return;
    }
    others.push(action);
  });
  return { shift, break: breakAction, others };
});

const shouldRenderStatusCard = computed(
  () => activeTabName.value === 'workforce_attendance'
);

const statusSummaryMetrics = computed(() => {
  const timezone = selfStatus.value?.session?.timezone || 'UTC';
  return [
    {
      id: 'state',
      label: t('WORKFORCE.STATUS.STATE_LABEL'),
      value: hasOpenSession.value
        ? t('WORKFORCE.STATUS.ON_DUTY')
        : t('WORKFORCE.STATUS.OFF_DUTY'),
      icon: hasOpenSession.value ? 'i-lucide-activity' : 'i-lucide-moon',
    },
    {
      id: 'shift_elapsed',
      label: t('WORKFORCE.STATUS.SHIFT_ELAPSED'),
      value: shiftDurationLabel.value,
      icon: 'i-lucide-hourglass',
    },
    {
      id: 'break_timer',
      label: t('WORKFORCE.STATUS.BREAK_TIMER'),
      value: activeBreakDurationLabel.value,
      icon: 'i-lucide-mug-hot',
    },
    {
      id: 'current_time',
      label: t('WORKFORCE.STATUS.CURRENT_TIME_LABEL'),
      value: currentTimeLabel.value,
      icon: 'i-lucide-clock-8',
      hint: t('WORKFORCE.STATUS.LOCAL_TIMEZONE_HINT', { tz: timezone }),
    },
    {
      id: 'next_shift',
      label: t('WORKFORCE.STATUS.NEXT_SHIFT_LABEL'),
      value: nextShiftEndLabel.value,
      icon: 'i-lucide-calendar-clock',
    },
  ];
});

const lastActionFeedback = ref(null);
let feedbackTimer = null;

const validateAction = key => {
  switch (key) {
    case 'check_in':
      if (hasOpenSession.value) {
        useAlert(t('WORKFORCE.TOASTS.SESSION_ALREADY_OPEN'));
        return false;
      }
      return true;
    case 'check_out':
      if (!hasOpenSession.value) {
        useAlert(t('WORKFORCE.TOASTS.NO_OPEN_SESSION'));
        return false;
      }
      return true;
    case 'start_break':
      if (!hasOpenSession.value) {
        useAlert(t('WORKFORCE.TOASTS.NO_OPEN_SESSION'));
        return false;
      }
      if (inBreak.value) {
        useAlert(t('WORKFORCE.TOASTS.BREAK_ALREADY_ACTIVE'));
        return false;
      }
      return true;
    case 'end_break':
      if (!hasOpenSession.value) {
        useAlert(t('WORKFORCE.TOASTS.NO_OPEN_SESSION'));
        return false;
      }
      if (!inBreak.value) {
        useAlert(t('WORKFORCE.TOASTS.BREAK_NOT_ACTIVE'));
        return false;
      }
      return true;
    default:
      return true;
  }
};

const successFeedbackMessages = {
  check_in: t('WORKFORCE.TOASTS.CHECK_IN_SUCCESS'),
  check_out: t('WORKFORCE.TOASTS.CHECK_OUT_SUCCESS'),
  start_break: t('WORKFORCE.TOASTS.START_BREAK_SUCCESS'),
  end_break: t('WORKFORCE.TOASTS.END_BREAK_SUCCESS'),
};

const performAction = async action => {
  if (!action?.handler || actionBusy.value) return;
  if (!validateAction(action.key)) return;
  try {
    await action.handler();
    if (feedbackTimer) {
      clearTimeout(feedbackTimer);
      feedbackTimer = null;
    }
    const feedbackId = Date.now();
    lastActionFeedback.value = {
      label: successFeedbackMessages[action.key] || action.label,
      id: feedbackId,
    };
    feedbackTimer = setTimeout(() => {
      if (lastActionFeedback.value?.id === feedbackId) {
        lastActionFeedback.value = null;
      }
    }, 2600);
  } catch (error) {
    lastActionFeedback.value = null;
    console.error('Attendance action error', error);
  }
};
</script>

<template>
  <div class="flex h-full flex-col overflow-hidden workforce-layout">
    <div class="workforce-surface">
      <!-- Settings-style header -->
      <header
        class="settings-header flex items-center justify-between px-8 lg:px-10 py-5"
      >
        <div class="flex items-center gap-3">
          <div
            class="flex size-10 shrink-0 items-center justify-center rounded-2xl border border-white/20 bg-white/10"
          >
            <span class="i-lucide-users size-5 text-n-slate-11" />
          </div>
          <div>
            <h1 class="m-0 text-xl font-semibold text-n-slate-12">
              {{ t('WORKFORCE.HEADER.TITLE') }}
            </h1>
            <p class="mb-0 mt-0.5 text-sm text-n-slate-11">
              {{ t('WORKFORCE.HEADER.DESCRIPTION') }}
            </p>
          </div>
        </div>

        <!-- Attendance action buttons — visible only on attendance tab -->
        <Transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="translate-x-2 opacity-0"
          enter-to-class="translate-x-0 opacity-100"
          leave-active-class="transition duration-150 ease-in"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div v-if="shouldRenderStatusCard" class="flex items-center gap-2">
            <button
              v-for="action in actionsList"
              :key="action.key"
              :disabled="actionBusy"
              class="attendance-action-btn"
              :class="[
                action.variant === 'ghost'
                  ? 'attendance-action-btn--ghost'
                  : 'attendance-action-btn--primary',
              ]"
              @click="performAction(action)"
            >
              <span class="size-4" :class="action.icon" />
              {{ action.label }}
            </button>
            <button
              class="attendance-action-btn attendance-action-btn--icon"
              :disabled="loadingSelfStatus"
              @click="fetchSelfStatus"
            >
              <span
                class="i-lucide-refresh-cw size-4"
                :class="{ 'animate-spin': loadingSelfStatus }"
              />
            </button>
          </div>
        </Transition>
      </header>
    </div>

    <!-- Main content area -->
    <main class="workforce-content flex-1 overflow-y-auto">
      <!-- Self-status card (attendance tab only) -->
      <div v-if="shouldRenderStatusCard" class="px-8 lg:px-10 pt-5">
        <WorkforceSelfStatusCard
          :status="selfStatus"
          :loading="loadingSelfStatus"
          :grouped-actions="groupedActions"
          :metrics="statusSummaryMetrics"
          :expanded="statusPanelExpanded"
          :busy="actionBusy"
          :has-open-session="hasOpenSession"
          @toggle="statusPanelExpanded = !statusPanelExpanded"
          @perform="performAction"
          @refresh="fetchSelfStatus"
        />
      </div>
      <RouterView @refresh-self-status="fetchSelfStatus" />
    </main>

    <!-- Action feedback toast -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="translate-y-2 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-2"
    >
      <div
        v-if="lastActionFeedback"
        class="pointer-events-none fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-2xl bg-black/80 px-4 py-2.5 text-xs font-semibold text-white shadow-lg"
      >
        <span class="i-lucide-sparkles text-sm" />
        <span>{{ lastActionFeedback.label }}</span>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.workforce-layout {
  background: var(--wlab-bg-primary);
}

.workforce-surface {
  background: linear-gradient(
    180deg,
    rgba(99, 102, 241, 0.08) 0%,
    rgba(99, 102, 241, 0.02) 100%
  );
}

.workforce-content {
  background: var(--wlab-bg-primary);
}

.settings-header {
  min-height: 76px;
}

/* Attendance action buttons */
.attendance-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  font-size: 0.8125rem;
  font-weight: 500;
  border-radius: 9999px;
  border: 1px solid rgba(148, 163, 184, 0.3);
  transition: all 0.15s ease;
  cursor: pointer;
  white-space: nowrap;
}

.attendance-action-btn--primary {
  background: rgba(255, 255, 255, 0.92);
  color: #0f172a;
  border-color: rgba(255, 255, 255, 0.7);
}

.attendance-action-btn--primary:hover {
  background: rgba(255, 255, 255, 1);
}

.attendance-action-btn--ghost {
  background: rgba(255, 255, 255, 0.08);
  color: var(--wlab-text-secondary);
}

.attendance-action-btn--ghost:hover {
  background: rgba(255, 255, 255, 0.16);
  color: var(--wlab-text-primary);
}

.attendance-action-btn--icon {
  padding: 7px;
  background: rgba(255, 255, 255, 0.08);
  color: var(--wlab-text-secondary);
}

.attendance-action-btn--icon:hover {
  background: rgba(255, 255, 255, 0.16);
  color: var(--wlab-text-primary);
}

.attendance-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
